import { DestinationTypeConfigHelper } from '../../create-destination/components/destination-settings-form/destination-type-config-helper';
import {
  DEFAULT_SNOWFLAKE_SCHEMA_NAME,
  SNOWFLAKE_DEFAULT_REGION_ID,
  SNOWFLAKE_HOST_URL_PREFIX,
  SNOWFLAKE_HOST_URL_PRIVATE_LINK_FRAGMENT,
  SNOWFLAKE_HOST_URL_SUFFIX,
  SnowflakeAuthenticationType
} from './constants';
import { extractAccountAndRegionFromSnowflakeHostURL } from './utils';
import { S3_BUCKET_REGIONS } from '../../../pipeline/source-config-by-type/S3Base/constants';

export class SnowflakeConfigHelper implements DestinationTypeConfigHelper {
  getDefaultConfig() {
    const authType = SnowflakeAuthenticationType.KEY_PAIR;

    return {
      schemaName: DEFAULT_SNOWFLAKE_SCHEMA_NAME,
      sanitizeName: true,
      isPrivateLink: false,
      authType,
      useCustomerS3Stage: false
    };
  }

  getConfigFromRawData(rawData: any) {
    const isCompleteUrl = rawData.url_type === 'complete_url' && rawData.url;

    let accountUrl;
    let accountName;
    let region;
    let isPrivateLink;

    if (isCompleteUrl) {
      accountUrl = `${SNOWFLAKE_HOST_URL_PREFIX}${rawData.url}`;
      accountName = rawData.url?.split('.')[0] || '';
      region = null;
      isPrivateLink = false;
    } else {
      const privateLinkComponent = rawData.is_private_link
        ? `.${SNOWFLAKE_HOST_URL_PRIVATE_LINK_FRAGMENT}`
        : '';
      accountUrl =
        rawData.account_name && rawData.region_id
          ? `${SNOWFLAKE_HOST_URL_PREFIX}${rawData.account_name}.${rawData.region_id}${privateLinkComponent}${SNOWFLAKE_HOST_URL_SUFFIX}`
          : undefined;
      accountName = rawData.account_name;
      region = rawData.region_id;
      isPrivateLink = rawData.is_private_link;
    }

    const config: any = {
      user: rawData.db_user,
      password: rawData.db_password,
      passphrase: rawData?.private_key_passphrase || '',
      databaseName: rawData.db_name,
      schemaName: rawData.schema_name,
      warehouseName: rawData.warehouse,
      isTableTypeTransient: rawData.is_table_type_transient,
      sanitizeName: rawData.sanitize_name,
      accountUrl,
      accountName,
      region,
      isPrivateLink,
      authType: !!rawData.private_key || rawData.private_key === ''
        ? SnowflakeAuthenticationType.KEY_PAIR
        : SnowflakeAuthenticationType.ACCESS_CREDENTIALS,
      useCustomerS3Stage: rawData.use_customer_s3_stage || false,
      storageIntegrationName: rawData.storage_integration_name || '',
      customerS3Bucket: rawData.customer_s3_bucket || '',
      customerS3Prefix: rawData.customer_s3_prefix || '',
      customerS3Region: S3_BUCKET_REGIONS.find(
        (region: any) => region.value === rawData.customer_s3_region
      ) || null,
      customerIamRoleArn: rawData.customer_iam_role_arn || '',
      customerExternalId: rawData.customer_external_id || ''
    };

    if ('private_key' in rawData && rawData.private_key !== '') {
      config.privateKey = { content: rawData?.private_key };
    }

    return config;
  }

  buildConnectionParams(data: any) {
    const params: any = {
      db_user: data.user,
      db_name: data.databaseName,
      warehouse: data.warehouseName,
      sanitize_name: data.sanitizeName,
      is_table_type_transient: data.isTableTypeTransient
    };

    if (data.authType === SnowflakeAuthenticationType.ACCESS_CREDENTIALS) {
      params.db_password = data.password;
    } else {
      params.private_key = data.privateKey.content;
      params.private_key_passphrase = data.passphrase || '';
    }

    if (data.schemaName) {
      params.schema_name = data.schemaName;
    }

    params.use_customer_s3_stage = data.useCustomerS3Stage || false;

    if (data.useCustomerS3Stage) {
      params.storage_integration_name = data.storageIntegrationName;
      params.customer_s3_bucket = data.customerS3Bucket;
      params.customer_s3_prefix = data.customerS3Prefix || '';
      params.customer_s3_region = data.customerS3Region?.value;
      params.customer_iam_role_arn = data.customerIamRoleArn;
      if (data.customerExternalId) {
        params.customer_external_id = data.customerExternalId;
      }
    }

    const isYukiUrl =
      data.yukiProxyEnabled === true ||
      data.accountUrl?.toLowerCase().endsWith('yukicomputing.com');

    if (isYukiUrl) {
      params.url_type = 'complete_url';
      params.url = data.accountUrl?.replace(/^https?:\/\//, '');
      params.is_private_link = data.isPrivateLink || false;
    } else {
      params.url_type = 'split_url';
      if (data.accountName && data.region) {
        params.account_name = data.accountName;
        params.region_id = data.region;
        params.is_private_link = data.isPrivateLink;
      } else {
        const { accountName, regionId, isPrivateLink } = extractAccountAndRegionFromSnowflakeHostURL(
          data.accountUrl
        );

        params.account_name = accountName;
        params.region_id = regionId || SNOWFLAKE_DEFAULT_REGION_ID;
        params.is_private_link = isPrivateLink;
      }
    }

    return params;
  }

  metaDataResolver() {
    return {};
  }
}
